import {
  Component,
  OnInit,
  Input,
  ViewChild,
  AfterViewInit,
  ElementRef,
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  Renderer2,
} from '@angular/core'
import * as Chart from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import 'chartjs-plugin-annotation'
import * as lodash from 'lodash'
import * as moment from 'moment'
import { Destroyable } from '@tokeet-frontend/tv3-platform'
import { ChannelMonthlyRatingDistItem, ChannelReviewsResponse } from '@tokeet-frontend/reviews'
import { ReviewMontlyAvgRatingTooltipComponent } from './tooltip/review-montly-avg-rating-tooltip.component'
import { getLocale } from '@tv3/utils/functions/get-locale'

const zeroLineAnnotation = {
  drawTime: 'afterDatasetsDraw',
  type: 'line',
  borderColor: 'gray',
  borderWidth: 0.5,
  mode: 'horizontal',
  scaleID: 'y-axis-0',
  value: 0,
  label: {
    backgroundColor: 'whiteSmoke',
    cornerRadius: 6,
    content: $localize`No Reviews`,
    fontSize: 40,
    fontStyle: 'normal',
    xPadding: 5,
    yPadding: 5,
    enabled: false,
  },
}

@Component({
  selector: 'app-review-montly-avg-rating',
  templateUrl: './review-montly-avg-rating.component.html',
  styleUrls: ['./review-montly-avg-rating.component.scss'],
})
export class ReviewMontlyAvgRatingComponent extends Destroyable implements OnInit, AfterViewInit {
  @ViewChild('ctx', { static: true }) ctx: ElementRef<HTMLCanvasElement>
  @ViewChild('chartContainer', { static: true }) chartContainer: ElementRef<HTMLDivElement>
  @ViewChild('tooltipContainer', { read: ViewContainerRef }) tooltipContainer: ViewContainerRef
  tooltipComRef: ComponentRef<ReviewMontlyAvgRatingTooltipComponent> = null

  @Input() set data(d: ChannelReviewsResponse) {
    if (d) {
      this.refresh(d)
    }
  }

  @Input() isLoading = false

  currentYear = moment().year()
  globalRating = 0
  statsByMonth: Record<string, ChannelMonthlyRatingDistItem> = {}
  statsByYear: Record<string, { year: number; count: number }> = {}
  // the data will be shown under the x-axis if the rating is less than 3. so the order is important
  ratingCategories = ['2-2.9', '0-1.9', '3-3.9', '4-4.4', '4.5-4.7', '4.8-5']

  labels = lodash
    .range(0, 12)
    .map((index) => {
      const d = moment().locale(getLocale()).subtract(index, 'month')
      return { date: d, key: d.format('YYYY-M'), label: d.format(d.isSame(moment(), 'year') ? 'MMM' : 'MMM,YYYY') }
    })
    .reverse()

  chart: Chart

  constructor(private resolver: ComponentFactoryResolver, private renderer: Renderer2) {
    super()
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.init()
    this.draw()
  }

  getCurrentYear5StarInc() {
    const s1 = this.statsByYear[this.currentYear]?.count || 0
    const s2 = this.statsByYear[this.currentYear - 1]?.count || 0

    return ((s1 - s2) / (s2 || 1)) * 100
  }

  refresh(d: ChannelReviewsResponse) {
    this.statsByMonth = lodash.keyBy(d?.monthlyRatingDistribution, (t) => t.month)
    this.statsByYear = lodash.keyBy(d?.yearlyFiveStarRating, (t) => t.year)
    this.globalRating =
      lodash.sumBy(lodash.toPairs(d?.averageRating), ([key, v]) => +v.averageRating) /
      (Object.keys(d?.averageRating).length || 1)
    if (this.chart) {
      this.draw()
    }
  }

  getChartData() {
    return {
      labels: [...this.labels.map((t) => t.label)],
      datasets: this.ratingCategories.map((cat, i) => ({
        label: cat,
        data: lodash
          .map(this.labels, (t) => lodash.find(this.statsByMonth[t.key]?.ratings, (r) => r.range == cat)?.count || 0)
          .map((n) => (i < 2 ? -n : n)),
        backgroundColor: ['#E29E8A', '#CB6A4D', '#74BBE2', '#46A4D9', '#188DCF', '#188DCF'][i],
        barPercentage: 0.99,
        categoryPercentage: 1,
      })),
    }
  }

  isDatasetEmpty() {
    return !lodash.find(this.chart?.data?.datasets, (d) => lodash.some(d.data, (t) => !!t))
  }

  draw() {
    this.chart.data = this.getChartData()
    if (this.isDatasetEmpty()) {
      zeroLineAnnotation.label.enabled = true
    } else {
      zeroLineAnnotation.label.enabled = false
    }
    // @ts-ignore
    this.chart.options.annotation.annotations = [zeroLineAnnotation]
    this.chart.update()
  }

  init() {
    const factory = this.resolver.resolveComponentFactory(ReviewMontlyAvgRatingTooltipComponent)
    this.tooltipComRef = this.tooltipContainer.createComponent(factory)
    this.chartContainer.nativeElement.addEventListener('mouseleave', () => {
      const tooltipElement = this.tooltipComRef.location.nativeElement
      this.renderer.setStyle(tooltipElement, 'display', 'none')
    })

    const self = this
    this.chart = new Chart(this.ctx.nativeElement, {
      type: 'bar',
      data: {},
      plugins: [ChartDataLabels],
      options: {
        legend: {
          display: false,
        },
        layout: {
          padding: {
            top: 30,
            bottom: 0,
            left: 0,
            right: 0,
          },
        },

        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              display: false,
              gridLines: {
                display: false,
              },
            },
          ],
        },
        tooltips: {
          enabled: false, // Disable default tooltips
          mode: 'index',

          custom: (tooltipModel) => {
            const tooltipElement = this.tooltipComRef.location.nativeElement

            const date = tooltipModel.dataPoints?.[0]?.xLabel
            const month = this.labels.find((t) => t.label === date)
            if (tooltipModel.opacity === 0 || !month) {
              this.renderer.setStyle(tooltipElement, 'display', 'none')

              return // Hide if not visible
            } else {
              this.renderer.setStyle(tooltipElement, 'display', 'block')
            }
            const currentMonth = month.date
            const preMonthKey = currentMonth.clone().locale(getLocale()).subtract(1, 'month').format('YYYY-M')

            this.tooltipComRef.instance.monthLabel = currentMonth.locale(getLocale()).format('MMMM - YYYY')
            this.tooltipComRef.instance.item = this.statsByMonth[month?.key]
            this.tooltipComRef.instance.previousItem = this.statsByMonth[preMonthKey]
            // Position the tooltip
            this.renderer.setStyle(tooltipElement, 'left', `${tooltipModel.caretX}px`)
            this.renderer.setStyle(tooltipElement, 'top', `${tooltipModel.caretY}px`)
          },
        },
        // @ts-ignore
        annotation: {
          annotations: [],
        },

        plugins: {
          datalabels: {
            align: 'end',
            anchor: 'end',
            color: function (context) {
              if (context.datasetIndex !== self.ratingCategories.length - 1) return ''
              const date = self.labels[context.dataIndex]
              const currentMonthRating = self.statsByMonth[date.key]?.averageOverallRating || 0
              const lastMonthRating =
                self.statsByMonth[date.date.clone().locale(getLocale()).subtract(1, 'month').format('YYYY-M')]
                  ?.averageOverallRating || 0
              if (!currentMonthRating) return ''
              if (currentMonthRating >= lastMonthRating) return `#03A99D`
              else return `#E95124`
            },
            font: function (context) {
              const w = context.chart.width
              return {
                size: w < 512 ? 12 : 14,
                // weight: 'bold',
              }
            },
            formatter: function (value, context) {
              if (context.datasetIndex !== self.ratingCategories.length - 1) return ''
              const date = self.labels[context.dataIndex]
              let currentMonthRating = self.statsByMonth[date.key]?.averageOverallRating || 0
              currentMonthRating = +Number(currentMonthRating).toFixed(2)
              const lastMonthRating =
                self.statsByMonth[date.date.clone().locale(getLocale()).subtract(1, 'month').format('YYYY-M')]
                  ?.averageOverallRating || 0
              if (!currentMonthRating) return ''
              if (currentMonthRating >= lastMonthRating) return `⬆${currentMonthRating}`
              else return `⬇${currentMonthRating}`
            },
          },
        },
      },
    })
  }
}
